/**
 * PIRATE FLEET — top-down naval combat with wind + boarding fork (MVP).
 *
 * Heritage: Sea Wolf (ARC 1976), Battlezone (ARC 1980), Adventure (A2600).
 *
 * Original mechanics:
 *   1. **Wind**: a slowly-rotating wind vector limits the player's max
 *      sailing speed based on heading relative to wind. Tailwind = 100%,
 *      beam reach = 70%, close-hauled = 40%, into the wind = 15%. Forces
 *      navigation, not point-and-shoot.
 *   2. **Broadside cannons**: Z fires port, X fires starboard. Player must
 *      turn the ship to bring a broadside to bear — combat verb is ship
 *      rotation.
 *   3. **Boarding fork**: ramming an enemy ship triggers boarding, which
 *      PRNG-routes 50/50 to either a sword duel or an artillery mini-game.
 *      Wind from the open-sea layer carries over into the artillery
 *      mini-game's projectile physics — skill bridge between layers.
 *
 * Phase state machine:
 *   open-sea → boarding-transition → duel | artillery → boarding-resolution
 *     → open-sea (or game-over)
 *
 * Determinism: all randomness via ctx.random; same (seed, inputFrames)
 * always yields same final score. Verified by 1000-iter test.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
} from '@arcraz/arcadia-types';
import {
  ART_BARREL,
  ART_BARREL_BAND,
  ART_HIT_FLASH,
  ART_SKY,
  ART_SKY_LIGHT,
  ART_TRAJECTORY_DOT,
  ART_WATER,
  CANNON_BALL,
  CANNON_BARREL,
  CHEST_BAND,
  CHEST_BODY,
  CHEST_GOLD,
  DUEL_CLASH_FLASH,
  DUEL_DECK,
  DUEL_DECK_DARK,
  DUEL_HORIZON,
  DUEL_PIRATE_ENEMY,
  DUEL_PIRATE_PLAYER,
  DUEL_SKY,
  DUEL_SKY_DARK,
  DUEL_SWORD,
  DUEL_SWORD_GLINT,
  ENEMY_FLAG,
  ENEMY_HULL,
  ENEMY_HULL_DARK,
  ENEMY_SAIL,
  ENEMY_SAIL_DARK,
  FLAG_BLUE,
  HUD_HP_FULL,
  HUD_HP_LOW,
  HUD_SCORE,
  HUD_TEXT,
  HULL_BODY,
  HULL_DARK,
  HULL_DECK,
  ISLAND_PALM,
  ISLAND_PALM_DARK,
  ISLAND_ROCK,
  ISLAND_SAND,
  ISLAND_SAND_DARK,
  SAIL_SHADOW,
  SAIL_WHITE,
  SEA_DARK,
  SEA_LIGHT,
  SEA_MID,
  SEA_WAKE,
  TRANSITION_BLACK,
  WIND_DIAL_ARROW,
  WIND_DIAL_BG,
  WIND_DIAL_RIM,
  WIND_DIAL_TEXT,
} from './palette.js';

const WIDTH = 480;
const HEIGHT = 270;
const HUD_H = 30;

// ============================================================================
// Open-sea constants
// ============================================================================

const PLAYER_SHIP_W = 22;
const BASE_SPEED = 2.0;
const TURN_RATE = 0.06; // radians/tick
const WIND_ROTATION_RATE = 0.002; // radians/tick — slow drift

// Wind speed factor by angular delta (radians) between ship heading + wind.
// Softened from the initial pass — playtest 2026-05-11 reported the
// original curve (1.0/0.7/0.4/0.15) made the ship "almost impossible
// to maneuver" when wind wasn't friendly. Into-wind now lets you crawl
// at 45% instead of 15%; close-hauled is 65% instead of 40%. Wind
// still meaningfully shapes navigation but doesn't gate movement.
function windSpeedFactor(deltaToTailwind: number): number {
  const d = Math.abs(normalizeAngle(deltaToTailwind));
  if (d < Math.PI / 6) return 1.0;        // tailwind ±30°
  if (d < Math.PI / 3) return 0.85;       // beam reach ±60°
  if (d < (5 * Math.PI) / 12) return 0.65; // close-hauled ±75°
  return 0.45;                             // into the wind
}

function normalizeAngle(a: number): number {
  let x = a;
  while (x > Math.PI) x -= 2 * Math.PI;
  while (x < -Math.PI) x += 2 * Math.PI;
  return x;
}

// Cannons.
const CANNON_COOLDOWN = 72; // ticks (~1.2s)
const CANNONBALL_SPEED = 4;
const CANNONBALL_LIFE = 90; // ticks before despawning
const CANNONBALL_R = 3;

// Enemies.
type EnemyKind = 'sloop' | 'frigate' | 'man-of-war';
const ENEMY_BASE_HP: Record<EnemyKind, number> = {
  sloop: 1,
  frigate: 3,
  'man-of-war': 6,
};
const ENEMY_BASE_SPEED: Record<EnemyKind, number> = {
  sloop: 1.3,
  frigate: 0.9,
  'man-of-war': 0.6,
};
const ENEMY_COOLDOWN: Record<EnemyKind, number> = {
  sloop: 110,
  frigate: 90,
  'man-of-war': 75,
};
const ENEMY_VOLLEY: Record<EnemyKind, number> = {
  sloop: 1,
  frigate: 3,
  'man-of-war': 5,
};
const ENEMY_SCORE: Record<EnemyKind, number> = {
  sloop: 100,
  frigate: 300,
  'man-of-war': 800,
};
const ENEMY_SPAWN_INTERVAL = 360; // ticks (~6s)
const MAX_ENEMIES = 5;

// Islands + treasure.
const ISLAND_SPAWN_INTERVAL = 540;
const MAX_ISLANDS = 3;
const TREASURE_PICKUP_RADIUS = 24;
const TREASURE_SCORE = 250;

// Player.
const PLAYER_MAX_HP = 3;
const PLAYER_REGEN_TICKS = 600; // 10s of no-damage to regen +1
const RAM_SPEED_THRESHOLD = 0.4; // must be moving forward this fast to ram

// Score.
const CANNONBALL_HIT_SCORE = 50;

// World extent — scrolls infinitely via wrap modulo. Player stays centered;
// "world" coords are virtual.
const WORLD_W = 1920;
const WORLD_H = 1080;

// ============================================================================
// Duel mini-game constants
// ============================================================================

const DUEL_DURATION_MAX = 1800; // 30s safety cap
const DUEL_HP_MAX = 3;
const DUEL_WINDUP_TICKS_MIN = 36;
const DUEL_WINDUP_TICKS_MAX = 72;
const DUEL_PARRY_WINDOW = 18; // ticks where X parries the enemy strike
const DUEL_STUMBLE_TICKS = 45; // after a successful parry, enemy is open this long
const DUEL_STRIKE_RECOVERY = 30; // player ticks after a strike before next action
const DUEL_PARRY_RECOVERY = 18;

type DuelEnemyState = 'idle' | 'winding-up' | 'stumbling' | 'recovering';

// ============================================================================
// Artillery mini-game constants
// ============================================================================

const ART_HP_MAX = 3;
const ART_PLAYER_X = 60;
const ART_ENEMY_X = WIDTH - 80;
const ART_GROUND_Y = HEIGHT - 50;
const ART_GRAVITY = 0.25;
const ART_MIN_ANGLE = 10; // degrees
const ART_MAX_ANGLE = 80;
const ART_MIN_POWER = 40;
const ART_MAX_POWER = 100;
const ART_BARRELS = 3;
const ART_BARREL_W = 14;
const ART_BARREL_H = 18;
const ART_SHIP_W = 36;
const ART_SHIP_H = 24;

type ArtTurn = 'player' | 'enemy' | 'projectile' | 'resolve';

// ============================================================================
// Types
// ============================================================================

type Phase =
  | 'open-sea'
  | 'boarding-transition-in' // screen wipes to black, then to mini-game
  | 'duel'
  | 'artillery'
  | 'boarding-transition-out' // screen wipes back to open-sea
  | 'game-over';

interface Vec {
  x: number;
  y: number;
}

interface Cannonball extends Vec {
  vx: number;
  vy: number;
  fromPlayer: boolean;
  life: number;
}

interface Enemy {
  x: number;
  y: number;
  heading: number; // radians
  hp: number;
  kind: EnemyKind;
  cooldown: number;
  /** Cached intent for AI: 0=approach, 1=fire, 2=drift. */
  aiPhase: number;
  aiTimer: number;
}

interface Island {
  x: number;
  y: number;
  /** Radius of the sand portion. */
  r: number;
  /** True if this island has an unclaimed treasure chest. */
  hasTreasure: boolean;
  /** Position of the chest relative to island center. */
  chestX: number;
  chestY: number;
}

interface Projectile {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

// ============================================================================
// Instance
// ============================================================================

export class PirateFleetInstance implements GameInstance {
  private readonly ctx: GameContext;

  // Phase.
  private phase: Phase = 'open-sea';
  private transitionTicks = 0;
  private transitionDuration = 20;

  // ------------------------------------------------------------------ open-sea
  // Player ship (always at screen center when in open-sea phase).
  private playerHeading = -Math.PI / 2; // facing up initially
  private playerSpeed = 0;
  /** Player world position (for wrap-around). */
  private playerWX = WORLD_W / 2;
  private playerWY = WORLD_H / 2;
  private playerHP = PLAYER_MAX_HP;
  private playerRegenTimer = 0;
  private portCooldown = 0;
  private starboardCooldown = 0;
  private invulnTicks = 0;

  // Wind.
  private windDirection = Math.PI; // angle the wind blows TOWARD
  private windRotationDir: 1 | -1 = 1;

  // Entities (positions in world coords).
  private enemies: Enemy[] = [];
  private cannonballs: Cannonball[] = [];
  private islands: Island[] = [];

  // Ocean texture — world-anchored specks that give the player an
  // optical-flow cue for motion. Without these, the sea reads as a
  // static blue field and the player can't tell they're moving.
  // Seeded once at construction; their positions don't change, but
  // because they're rendered in world-space, they appear to slide
  // past as the player's world position updates.
  private seaSpecks: ReadonlyArray<{ x: number; y: number; bright: boolean }> = [];
  // Wave streaks — periodically spawned short bright marks oriented
  // perpendicular to wind, drifting in the wind direction. Gives the
  // ocean visible wind-flow.
  private waveStreaks: Array<{ x: number; y: number; life: number }> = [];
  private waveStreakTimer = 0;

  // Spawn timers.
  private enemySpawnTimer = 180;
  private islandSpawnTimer = 60;

  // Score.
  private score = 0;

  // Damage feedback.
  private hitFlashLeft = 0;

  // Pending boarding context (which enemy initiated boarding).
  private boardingEnemy: Enemy | null = null;

  // ------------------------------------------------------------------ duel
  private duelPlayerHP = 0;
  private duelEnemyHP = 0;
  private duelPlayerRecovery = 0;
  private duelEnemyState: DuelEnemyState = 'idle';
  private duelEnemyTimer = 0;
  private duelEnemyWindupDuration = 0;
  private duelPlayerWon = false;
  private duelTimeUsed = 0;
  private duelClashFlash = 0;

  // ------------------------------------------------------------------ artillery
  private artPlayerHP = 0;
  private artEnemyHP = 0;
  private artTurn: ArtTurn = 'player';
  private artAngle = 45; // degrees
  private artPower = 70;
  private artProjectile: Projectile | null = null;
  private artBarrels: Array<{ x: number; alive: boolean }> = [];
  private artHitFlash = 0;
  private artResolveTimer = 0;
  private artPlayerWon = false;

  // ------------------------------------------------------------------ game-over
  private finalScore = 0;
  private restartArmed = false;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    // Seed initial wind from PRNG so different runs have different opening winds.
    this.windDirection = (ctx.random.next() * 2 - 1) * Math.PI;
    this.windRotationDir = ctx.random.next() < 0.5 ? 1 : -1;
    this.seedSeaSpecks();
  }

  private seedSeaSpecks(): void {
    // Sprinkle specks across the entire toroidal world. ~600 specks over
    // 1920x1080 = roughly 1 every ~75px² of world space, which renders to
    // about 80-100 visible specks on screen at any moment — sparse enough
    // to read as "texture on water" not "static."
    const specks: Array<{ x: number; y: number; bright: boolean }> = [];
    for (let i = 0; i < 600; i++) {
      specks.push({
        x: this.ctx.random.next() * WORLD_W,
        y: this.ctx.random.next() * WORLD_H,
        bright: this.ctx.random.next() < 0.25, // 25% are slightly brighter
      });
    }
    this.seaSpecks = specks;
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA')) this.restartArmed = true;
      return;
    }
    if (this.phase === 'open-sea') {
      this.openSeaInput(state);
    } else if (this.phase === 'duel') {
      this.duelInput(state);
    } else if (this.phase === 'artillery') {
      this.artilleryInput(state);
    }
  }

  update(_dt: number): void {
    if (this.phase === 'game-over') return;

    if (this.phase === 'boarding-transition-in') {
      this.transitionTicks += 1;
      if (this.transitionTicks >= this.transitionDuration) {
        // Roll the boarding fork — PRNG 50/50.
        const roll = this.ctx.random.next();
        if (roll < 0.5) this.startDuel();
        else this.startArtillery();
      }
      return;
    }
    if (this.phase === 'boarding-transition-out') {
      this.transitionTicks += 1;
      if (this.transitionTicks >= this.transitionDuration) {
        this.phase = 'open-sea';
        this.boardingEnemy = null;
      }
      return;
    }

    if (this.phase === 'open-sea') this.updateOpenSea();
    else if (this.phase === 'duel') this.updateDuel();
    else if (this.phase === 'artillery') this.updateArtillery();
  }

  render(g: CanvasRenderingContext2D): void {
    if (this.phase === 'open-sea' || this.phase === 'boarding-transition-in') {
      this.renderOpenSea(g);
    } else if (this.phase === 'duel') {
      this.renderDuel(g);
    } else if (this.phase === 'artillery') {
      this.renderArtillery(g);
    } else if (this.phase === 'boarding-transition-out') {
      // Render whichever mini-game we're leaving from, faded to black.
      this.renderOpenSea(g);
    }

    // Transition fade overlay.
    if (
      this.phase === 'boarding-transition-in' ||
      this.phase === 'boarding-transition-out'
    ) {
      const t = this.transitionTicks / this.transitionDuration;
      const a = this.phase === 'boarding-transition-in' ? t : 1 - t;
      g.globalAlpha = Math.min(1, Math.max(0, a));
      g.fillStyle = TRANSITION_BLACK;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    if (this.phase === 'game-over') this.renderGameOver(g);
  }

  resize(_w: number, _h: number): void {
    /* 480x270 internal */
  }
  pause(): void {
    /* no continuous loops to suspend */
  }
  resume(): void {
    /* no-op */
  }
  teardown(): void {
    this.enemies = [];
    this.cannonballs = [];
    this.islands = [];
    this.artBarrels = [];
  }
  getScore(): number {
    return Math.floor(this.score);
  }
  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : Math.floor(this.score);
  }
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }

  // ==========================================================================
  // OPEN-SEA PHASE
  // ==========================================================================

  private openSeaInput(state: InputState): void {
    if (state.left) this.playerHeading -= TURN_RATE;
    if (state.right) this.playerHeading += TURN_RATE;
    this.playerHeading = normalizeAngle(this.playerHeading);

    // UP = trim sails (full power). DOWN = furl (slow). Faster ramp so
    // the ship feels responsive on press; gentle decay so you can still
    // coast and turn without immediately stalling.
    if (state.up) this.playerSpeed = Math.min(1, this.playerSpeed + 0.08);
    else if (state.down) this.playerSpeed = Math.max(0, this.playerSpeed - 0.06);
    else this.playerSpeed = Math.max(0, this.playerSpeed - 0.01);

    // Either Z or X fires BOTH broadsides simultaneously — full volley.
    // Each side has its own cooldown so a back-to-back press where only
    // one side was ready will still fire the ready side.
    if (state.justPressed.has('fireA') || state.justPressed.has('fireB')) {
      if (this.portCooldown <= 0) this.fireBroadside('port');
      if (this.starboardCooldown <= 0) this.fireBroadside('starboard');
    }
  }

  private updateOpenSea(): void {
    // Wind drift.
    this.windDirection = normalizeAngle(
      this.windDirection + WIND_ROTATION_RATE * this.windRotationDir
    );

    // Compute player velocity from heading + wind factor + speed slider.
    const tailwindHeading = normalizeAngle(this.windDirection); // wind blows TOWARD here
    const delta = normalizeAngle(this.playerHeading - tailwindHeading);
    const windFactor = windSpeedFactor(delta);
    const effectiveSpeed = BASE_SPEED * this.playerSpeed * windFactor;
    const vx = Math.cos(this.playerHeading) * effectiveSpeed;
    const vy = Math.sin(this.playerHeading) * effectiveSpeed;
    this.playerWX = (this.playerWX + vx + WORLD_W) % WORLD_W;
    this.playerWY = (this.playerWY + vy + WORLD_H) % WORLD_H;

    // Cooldowns + flashes + regen.
    if (this.portCooldown > 0) this.portCooldown -= 1;
    if (this.starboardCooldown > 0) this.starboardCooldown -= 1;
    if (this.invulnTicks > 0) this.invulnTicks -= 1;
    if (this.hitFlashLeft > 0) this.hitFlashLeft -= 1;
    this.playerRegenTimer += 1;

    // Wave streaks — visible wind-flow markers. Spawn one near the player
    // every ~6 ticks; each lives ~80 ticks while drifting downwind. Keeps
    // the population around 13-14 streaks on screen at any moment.
    this.waveStreakTimer -= 1;
    if (this.waveStreakTimer <= 0) {
      // Spawn somewhere in a 360x300 box centered on the player so they
      // appear ahead/around, not just behind.
      const spawnX = this.playerWX + (this.ctx.random.next() - 0.5) * 360;
      const spawnY = this.playerWY + (this.ctx.random.next() - 0.5) * 300;
      this.waveStreaks.push({
        x: (spawnX + WORLD_W) % WORLD_W,
        y: (spawnY + WORLD_H) % WORLD_H,
        life: 80,
      });
      this.waveStreakTimer = 6;
    }
    // Drift + age streaks. Wind direction is the angle the wind blows
    // TOWARD, so streaks drift in that direction.
    const windVx = Math.cos(this.windDirection) * 0.6;
    const windVy = Math.sin(this.windDirection) * 0.6;
    const keptStreaks: typeof this.waveStreaks = [];
    for (const s of this.waveStreaks) {
      s.x = (s.x + windVx + WORLD_W) % WORLD_W;
      s.y = (s.y + windVy + WORLD_H) % WORLD_H;
      s.life -= 1;
      if (s.life > 0) keptStreaks.push(s);
    }
    this.waveStreaks = keptStreaks;
    if (this.playerRegenTimer >= PLAYER_REGEN_TICKS && this.playerHP < PLAYER_MAX_HP) {
      this.playerHP += 1;
      this.playerRegenTimer = 0;
    }

    // Spawns.
    this.enemySpawnTimer -= 1;
    if (this.enemySpawnTimer <= 0 && this.enemies.length < MAX_ENEMIES) {
      this.spawnEnemy();
      this.enemySpawnTimer = ENEMY_SPAWN_INTERVAL;
    }
    this.islandSpawnTimer -= 1;
    if (this.islandSpawnTimer <= 0 && this.islands.length < MAX_ISLANDS) {
      this.spawnIsland();
      this.islandSpawnTimer = ISLAND_SPAWN_INTERVAL;
    }

    // Cull islands that drift too far from player (off-world wrap-around).
    this.islands = this.islands.filter((isl) => {
      const dx = this.worldDeltaX(isl.x, this.playerWX);
      const dy = this.worldDeltaY(isl.y, this.playerWY);
      return Math.hypot(dx, dy) < WORLD_W / 3;
    });

    // Enemy AI + collision.
    this.updateEnemies();

    // Cannonballs.
    this.updateCannonballs();

    // Treasure pickup.
    for (const isl of this.islands) {
      if (!isl.hasTreasure) continue;
      const cx = isl.x + isl.chestX;
      const cy = isl.y + isl.chestY;
      const dx = this.worldDeltaX(cx, this.playerWX);
      const dy = this.worldDeltaY(cy, this.playerWY);
      if (Math.hypot(dx, dy) < TREASURE_PICKUP_RADIUS) {
        isl.hasTreasure = false;
        this.score += TREASURE_SCORE;
        this.ctx.audio.sfx('treasure');
        this.ctx.events.emit('score', {
          score: Math.floor(this.score),
          delta: TREASURE_SCORE,
        });
      }
    }

    // Ramming check — moving forward fast enough + ship overlaps.
    if (effectiveSpeed >= RAM_SPEED_THRESHOLD) {
      for (const e of this.enemies) {
        const dx = this.worldDeltaX(e.x, this.playerWX);
        const dy = this.worldDeltaY(e.y, this.playerWY);
        if (Math.hypot(dx, dy) < PLAYER_SHIP_W * 0.9) {
          this.triggerBoarding(e);
          return;
        }
      }
    }
  }

  private fireBroadside(side: 'port' | 'starboard'): void {
    // Port = 90° to the LEFT of heading; starboard = 90° to the RIGHT.
    // In our coordinate system (y grows down), +90° from heading is screen-right.
    const sideAngle =
      side === 'port' ? this.playerHeading - Math.PI / 2 : this.playerHeading + Math.PI / 2;
    // Fire 3 cannonballs in a small fan for "broadside" effect.
    for (let i = -1; i <= 1; i++) {
      const spread = i * 0.08;
      const a = sideAngle + spread;
      this.cannonballs.push({
        x: this.playerWX + Math.cos(this.playerHeading) * 4 * i,
        y: this.playerWY + Math.sin(this.playerHeading) * 4 * i,
        vx: Math.cos(a) * CANNONBALL_SPEED,
        vy: Math.sin(a) * CANNONBALL_SPEED,
        fromPlayer: true,
        life: CANNONBALL_LIFE,
      });
    }
    if (side === 'port') this.portCooldown = CANNON_COOLDOWN;
    else this.starboardCooldown = CANNON_COOLDOWN;
    this.ctx.audio.sfx('cannon');
  }

  private updateEnemies(): void {
    for (const e of this.enemies) {
      const dx = this.worldDeltaX(e.x, this.playerWX);
      const dy = this.worldDeltaY(e.y, this.playerWY);
      const distToPlayer = Math.hypot(dx, dy);

      // Turn toward player when far, broadside when close.
      const wantBroadside = distToPlayer < 140;
      const angleToPlayer = Math.atan2(dy, dx) + Math.PI; // direction enemy SHOULD face to look at player
      let targetHeading: number;
      if (wantBroadside) {
        // Aim perpendicular to player so the broadside faces them.
        targetHeading = angleToPlayer + Math.PI / 2;
      } else {
        targetHeading = angleToPlayer;
      }
      const headingDelta = normalizeAngle(targetHeading - e.heading);
      const turn = Math.sign(headingDelta) * Math.min(Math.abs(headingDelta), TURN_RATE * 0.7);
      e.heading = normalizeAngle(e.heading + turn);

      // Move forward.
      const speed = ENEMY_BASE_SPEED[e.kind] * (e.hp < ENEMY_BASE_HP[e.kind] / 2 ? 1.2 : 1);
      e.x += Math.cos(e.heading) * speed;
      e.y += Math.sin(e.heading) * speed;

      // Fire when in range + facing approximately broadside-correct.
      e.cooldown -= 1;
      const facingDelta = Math.abs(normalizeAngle(e.heading - (angleToPlayer + Math.PI / 2)));
      if (e.cooldown <= 0 && distToPlayer < 200 && facingDelta < Math.PI / 6) {
        this.enemyFireVolley(e);
        e.cooldown = ENEMY_COOLDOWN[e.kind];
      }
    }
  }

  private enemyFireVolley(e: Enemy): void {
    const count = ENEMY_VOLLEY[e.kind];
    // Fire toward the player.
    const dx = this.worldDeltaX(this.playerWX, e.x);
    const dy = this.worldDeltaY(this.playerWY, e.y);
    const aim = Math.atan2(dy, dx);
    for (let i = 0; i < count; i++) {
      const spread = (i - (count - 1) / 2) * 0.08;
      const a = aim + spread + (this.ctx.random.next() - 0.5) * 0.04;
      this.cannonballs.push({
        x: e.x,
        y: e.y,
        vx: Math.cos(a) * (CANNONBALL_SPEED * 0.85),
        vy: Math.sin(a) * (CANNONBALL_SPEED * 0.85),
        fromPlayer: false,
        life: CANNONBALL_LIFE,
      });
    }
  }

  private updateCannonballs(): void {
    const kept: Cannonball[] = [];
    for (const b of this.cannonballs) {
      b.x += b.vx;
      b.y += b.vy;
      b.life -= 1;
      if (b.life <= 0) continue;

      if (b.fromPlayer) {
        // Hit-test against enemies.
        let hit = false;
        for (const e of this.enemies) {
          const dx = this.worldDeltaX(e.x, b.x);
          const dy = this.worldDeltaY(e.y, b.y);
          if (Math.hypot(dx, dy) < 12) {
            e.hp -= 1;
            this.score += CANNONBALL_HIT_SCORE;
            this.ctx.audio.sfx('art-hit');
            hit = true;
            break;
          }
        }
        if (hit) continue;
      } else {
        // Hit-test against player.
        const dx = this.worldDeltaX(this.playerWX, b.x);
        const dy = this.worldDeltaY(this.playerWY, b.y);
        if (Math.hypot(dx, dy) < 11 && this.invulnTicks <= 0) {
          this.takePlayerDamage(1);
          continue;
        }
      }
      kept.push(b);
    }
    this.cannonballs = kept;

    // Sink dead enemies + score.
    const aliveEnemies: Enemy[] = [];
    for (const e of this.enemies) {
      if (e.hp <= 0) {
        this.score += ENEMY_SCORE[e.kind];
        this.ctx.audio.sfx('sink');
        this.ctx.events.emit('score', {
          score: Math.floor(this.score),
          delta: ENEMY_SCORE[e.kind],
        });
      } else {
        aliveEnemies.push(e);
      }
    }
    this.enemies = aliveEnemies;
  }

  private takePlayerDamage(amount: number): void {
    this.playerHP -= amount;
    this.playerRegenTimer = 0;
    this.invulnTicks = 30;
    this.hitFlashLeft = 12;
    this.ctx.audio.sfx('wood-crack');
    if (this.playerHP <= 0) {
      this.phase = 'game-over';
      this.finalScore = Math.floor(this.score);
      this.ctx.events.emit('death', { remainingLives: 0 });
    }
  }

  private spawnEnemy(): void {
    // Spawn just off-screen, somewhere around the player at ~250 units out.
    const angle = this.ctx.random.next() * Math.PI * 2;
    const dist = 250;
    const x = (this.playerWX + Math.cos(angle) * dist + WORLD_W) % WORLD_W;
    const y = (this.playerWY + Math.sin(angle) * dist + WORLD_H) % WORLD_H;
    const roll = this.ctx.random.next();
    const kind: EnemyKind =
      roll < 0.6 ? 'sloop' : roll < 0.93 ? 'frigate' : 'man-of-war';
    this.enemies.push({
      x,
      y,
      heading: this.ctx.random.next() * Math.PI * 2,
      hp: ENEMY_BASE_HP[kind],
      kind,
      cooldown: 60,
      aiPhase: 0,
      aiTimer: 0,
    });
  }

  private spawnIsland(): void {
    const angle = this.ctx.random.next() * Math.PI * 2;
    const dist = 180 + this.ctx.random.next() * 80;
    const x = (this.playerWX + Math.cos(angle) * dist + WORLD_W) % WORLD_W;
    const y = (this.playerWY + Math.sin(angle) * dist + WORLD_H) % WORLD_H;
    const r = 22 + this.ctx.random.next() * 18;
    // 70% chance an island has treasure.
    const hasTreasure = this.ctx.random.next() < 0.7;
    this.islands.push({
      x,
      y,
      r,
      hasTreasure,
      chestX: (this.ctx.random.next() - 0.5) * r * 0.6,
      chestY: (this.ctx.random.next() - 0.5) * r * 0.6,
    });
  }

  private triggerBoarding(enemy: Enemy): void {
    this.boardingEnemy = enemy;
    this.phase = 'boarding-transition-in';
    this.transitionTicks = 0;
    this.transitionDuration = 24;
    this.ctx.audio.sfx('boarding');
  }

  /** World delta accounting for toroidal wrap. */
  private worldDeltaX(a: number, b: number): number {
    let d = a - b;
    if (d > WORLD_W / 2) d -= WORLD_W;
    if (d < -WORLD_W / 2) d += WORLD_W;
    return d;
  }
  private worldDeltaY(a: number, b: number): number {
    let d = a - b;
    if (d > WORLD_H / 2) d -= WORLD_H;
    if (d < -WORLD_H / 2) d += WORLD_H;
    return d;
  }

  // ==========================================================================
  // OPEN-SEA RENDER
  // ==========================================================================

  private renderOpenSea(g: CanvasRenderingContext2D): void {
    // Sea background with subtle horizontal bands for depth.
    g.fillStyle = SEA_MID;
    g.fillRect(0, 0, WIDTH, HEIGHT);
    g.fillStyle = SEA_DARK;
    for (let y = 12; y < HEIGHT; y += 24) {
      g.fillRect(0, y, WIDTH, 2);
    }
    g.fillStyle = SEA_LIGHT;
    for (let y = 0; y < HEIGHT; y += 24) {
      g.fillRect(0, y, WIDTH, 1);
    }

    // World-anchored sea specks. These give the player an optical-flow
    // cue for motion — they slide across the screen as the player's world
    // position updates. Two brightness tiers for subtle variation.
    for (const s of this.seaSpecks) {
      const sx = WIDTH / 2 + this.worldDeltaX(s.x, this.playerWX);
      const sy = HEIGHT / 2 + this.worldDeltaY(s.y, this.playerWY);
      if (sx < 0 || sx > WIDTH || sy < HUD_H || sy > HEIGHT) continue;
      g.fillStyle = s.bright ? SEA_WAKE : SEA_DARK;
      g.fillRect(Math.floor(sx), Math.floor(sy), 1, 1);
    }

    // Wave streaks — short drifting marks oriented perpendicular to wind.
    // These tell the player which way the wind is blowing on the water
    // itself, not just from the HUD compass.
    const streakAng = this.windDirection + Math.PI / 2; // perpendicular
    const sax = Math.cos(streakAng) * 3;
    const say = Math.sin(streakAng) * 3;
    for (const wv of this.waveStreaks) {
      const sx = WIDTH / 2 + this.worldDeltaX(wv.x, this.playerWX);
      const sy = HEIGHT / 2 + this.worldDeltaY(wv.y, this.playerWY);
      if (sx < -10 || sx > WIDTH + 10 || sy < HUD_H || sy > HEIGHT) continue;
      // Fade in/out at the edges of life.
      const fade = wv.life > 60 ? (80 - wv.life) / 20 : wv.life < 20 ? wv.life / 20 : 1;
      g.globalAlpha = Math.max(0, Math.min(1, fade)) * 0.7;
      g.fillStyle = SEA_WAKE;
      g.fillRect(Math.floor(sx - sax), Math.floor(sy - say), 2, 2);
      g.fillRect(Math.floor(sx + sax), Math.floor(sy + say), 2, 2);
      g.globalAlpha = 1;
    }

    // Wake trail behind player — longer tail so the player can see their
    // own velocity vector even when other cues fail.
    if (this.playerSpeed > 0.2) {
      const tailAng = this.playerHeading + Math.PI;
      for (let i = 1; i < 8; i++) {
        const tx = WIDTH / 2 + Math.cos(tailAng) * 6 * i;
        const ty = HEIGHT / 2 + Math.sin(tailAng) * 6 * i;
        // Fade the tail toward the end.
        const alpha = (1 - i / 8) * Math.min(1, this.playerSpeed * 1.5);
        g.globalAlpha = alpha;
        g.fillStyle = SEA_WAKE;
        g.fillRect(Math.floor(tx) - 1, Math.floor(ty) - 1, 2, 2);
      }
      g.globalAlpha = 1;
    }

    // Islands (world-to-screen).
    for (const isl of this.islands) {
      const sx = WIDTH / 2 + this.worldDeltaX(isl.x, this.playerWX);
      const sy = HEIGHT / 2 + this.worldDeltaY(isl.y, this.playerWY);
      if (sx < -isl.r * 2 || sx > WIDTH + isl.r * 2) continue;
      if (sy < -isl.r * 2 || sy > HEIGHT + isl.r * 2) continue;
      this.drawIsland(g, sx, sy, isl);
    }

    // Cannonballs.
    for (const b of this.cannonballs) {
      const sx = WIDTH / 2 + this.worldDeltaX(b.x, this.playerWX);
      const sy = HEIGHT / 2 + this.worldDeltaY(b.y, this.playerWY);
      if (sx < 0 || sx > WIDTH || sy < 0 || sy > HEIGHT) continue;
      g.fillStyle = CANNON_BALL;
      g.fillRect(Math.floor(sx) - CANNONBALL_R, Math.floor(sy) - CANNONBALL_R, CANNONBALL_R * 2, CANNONBALL_R * 2);
    }

    // Enemy ships.
    for (const e of this.enemies) {
      const sx = WIDTH / 2 + this.worldDeltaX(e.x, this.playerWX);
      const sy = HEIGHT / 2 + this.worldDeltaY(e.y, this.playerWY);
      if (sx < -40 || sx > WIDTH + 40 || sy < -40 || sy > HEIGHT + 40) continue;
      this.drawShip(g, sx, sy, e.heading, /*friendly=*/ false, e.kind);
    }

    // Player ship — always centered.
    this.drawShip(g, WIDTH / 2, HEIGHT / 2, this.playerHeading, true, 'sloop');

    // Damage flash overlay.
    if (this.hitFlashLeft > 0) {
      g.globalAlpha = (this.hitFlashLeft / 12) * 0.4;
      g.fillStyle = HUD_HP_LOW;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    this.renderHUD(g);
  }

  private drawIsland(g: CanvasRenderingContext2D, sx: number, sy: number, isl: Island): void {
    // Sand circle (drawn as a chunky rect approximation for pixel feel).
    g.fillStyle = ISLAND_SAND_DARK;
    g.beginPath();
    g.arc(sx, sy, isl.r, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = ISLAND_SAND;
    g.beginPath();
    g.arc(sx, sy, isl.r - 3, 0, Math.PI * 2);
    g.fill();
    // Rock outcrop.
    g.fillStyle = ISLAND_ROCK;
    g.fillRect(sx - 4, sy - 3, 8, 6);
    // Palm trunk + fronds.
    g.fillStyle = ISLAND_PALM_DARK;
    g.fillRect(sx + isl.r * 0.3, sy - 8, 2, 8);
    g.fillStyle = ISLAND_PALM;
    g.fillRect(sx + isl.r * 0.3 - 4, sy - 12, 10, 4);
    // Treasure chest.
    if (isl.hasTreasure) {
      const cx = sx + isl.chestX;
      const cy = sy + isl.chestY;
      g.fillStyle = CHEST_BAND;
      g.fillRect(cx - 5, cy - 4, 10, 8);
      g.fillStyle = CHEST_BODY;
      g.fillRect(cx - 4, cy - 3, 8, 6);
      g.fillStyle = CHEST_GOLD;
      g.fillRect(cx - 1, cy - 1, 2, 2);
    }
  }

  private drawShip(
    g: CanvasRenderingContext2D,
    sx: number,
    sy: number,
    heading: number,
    friendly: boolean,
    kind: EnemyKind
  ): void {
    g.save();
    g.translate(sx, sy);
    g.rotate(heading);
    const w = kind === 'man-of-war' ? 30 : kind === 'frigate' ? 24 : 18;
    const h = kind === 'man-of-war' ? 16 : kind === 'frigate' ? 14 : 12;
    // Shadow.
    g.fillStyle = '#000';
    g.fillRect(-w / 2 + 1, -h / 2 + 1, w, h);
    // Hull body.
    g.fillStyle = friendly ? HULL_BODY : ENEMY_HULL;
    g.fillRect(-w / 2, -h / 2, w, h);
    // Hull dark band.
    g.fillStyle = friendly ? HULL_DARK : ENEMY_HULL_DARK;
    g.fillRect(-w / 2, h / 2 - 3, w, 3);
    // Deck stripe.
    g.fillStyle = HULL_DECK;
    g.fillRect(-w / 2 + 2, -h / 2 + 3, w - 4, h - 6);
    // Bow point (forward).
    g.fillStyle = friendly ? HULL_BODY : ENEMY_HULL;
    g.fillRect(w / 2, -2, 3, 4);
    // Sail.
    g.fillStyle = friendly ? SAIL_WHITE : ENEMY_SAIL;
    g.fillRect(-3, -h / 2 - 5, 6, 8);
    g.fillStyle = friendly ? SAIL_SHADOW : ENEMY_SAIL_DARK;
    g.fillRect(-3, -h / 2 - 1, 6, 4);
    // Flag.
    g.fillStyle = friendly ? FLAG_BLUE : ENEMY_FLAG;
    g.fillRect(-1, -h / 2 - 8, 4, 3);
    g.restore();
  }

  // ==========================================================================
  // HUD (open-sea + boarding-transition)
  // ==========================================================================

  private renderHUD(g: CanvasRenderingContext2D): void {
    // HUD strip at top.
    g.fillStyle = 'rgba(10, 24, 40, 0.85)';
    g.fillRect(0, 0, WIDTH, HUD_H);
    g.fillStyle = WIND_DIAL_RIM;
    g.fillRect(0, HUD_H - 1, WIDTH, 1);

    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';
    g.textAlign = 'left';
    g.fillStyle = HUD_TEXT;
    g.fillText('SCORE', 8, 4);
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 8, 16);

    // HP center-left, hearts.
    g.textAlign = 'center';
    g.fillStyle = HUD_TEXT;
    g.fillText('HULL', 130, 4);
    for (let i = 0; i < PLAYER_MAX_HP; i++) {
      const filled = i < this.playerHP;
      g.fillStyle = filled
        ? this.playerHP === 1
          ? HUD_HP_LOW
          : HUD_HP_FULL
        : 'rgba(255,255,255,0.2)';
      g.fillRect(118 + i * 10, 17, 6, 8);
    }

    // Wind dial center-right.
    this.drawWindDial(g, WIDTH - 110, HUD_H / 2);

    g.textAlign = 'left';
  }

  private drawWindDial(g: CanvasRenderingContext2D, cx: number, cy: number): void {
    g.fillStyle = WIND_DIAL_BG;
    g.beginPath();
    g.arc(cx, cy, 12, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = WIND_DIAL_RIM;
    g.lineWidth = 1;
    g.stroke();
    // Arrow points in wind direction.
    g.save();
    g.translate(cx, cy);
    g.rotate(this.windDirection);
    g.fillStyle = WIND_DIAL_ARROW;
    g.fillRect(-1, -10, 2, 10);
    g.beginPath();
    g.moveTo(-4, -6);
    g.lineTo(4, -6);
    g.lineTo(0, -12);
    g.closePath();
    g.fill();
    g.restore();
    // "WIND" label.
    g.font = '8px ui-monospace, monospace';
    g.textAlign = 'right';
    g.fillStyle = WIND_DIAL_TEXT;
    g.fillText('WIND', cx + 32, cy - 5);
    // Heading-vs-wind indicator: tailwind/beam/etc.
    const delta = normalizeAngle(this.playerHeading - this.windDirection);
    const factor = windSpeedFactor(delta);
    const label =
      factor === 1 ? 'TAILWIND' : factor === 0.7 ? 'BEAM' : factor === 0.4 ? 'CLOSE' : 'STALL';
    g.fillStyle = factor >= 0.7 ? HUD_TEXT : factor >= 0.4 ? HUD_SCORE : HUD_HP_LOW;
    g.fillText(label, cx + 32, cy + 4);
  }

  // ==========================================================================
  // SWORD DUEL PHASE
  // ==========================================================================

  private startDuel(): void {
    this.phase = 'duel';
    this.duelPlayerHP = DUEL_HP_MAX;
    this.duelEnemyHP = DUEL_HP_MAX;
    this.duelPlayerRecovery = 0;
    this.duelEnemyState = 'idle';
    this.duelEnemyTimer = 60; // pause before first strike
    this.duelEnemyWindupDuration = 0;
    this.duelPlayerWon = false;
    this.duelTimeUsed = 0;
    this.duelClashFlash = 0;
  }

  private duelInput(state: InputState): void {
    if (this.duelPlayerRecovery > 0) return;

    if (state.justPressed.has('fireA')) {
      // Strike.
      if (this.duelEnemyState === 'stumbling') {
        this.duelEnemyHP -= 1;
        this.ctx.audio.sfx('strike');
        this.duelClashFlash = 8;
        this.duelPlayerRecovery = DUEL_STRIKE_RECOVERY;
        if (this.duelEnemyHP <= 0) {
          this.duelPlayerWon = true;
          this.endDuel();
        } else {
          // Reset enemy to idle, start next windup.
          this.duelEnemyState = 'idle';
          this.duelEnemyTimer = 60 + this.ctx.random.int(0, 60);
        }
      } else {
        // Strike on guard — clash, no damage to either, but player commits recovery.
        this.ctx.audio.sfx('clash');
        this.duelClashFlash = 6;
        this.duelPlayerRecovery = DUEL_STRIKE_RECOVERY;
      }
    } else if (state.justPressed.has('fireB')) {
      // Parry.
      if (this.duelEnemyState === 'winding-up') {
        // Successful parry — open enemy to a strike.
        const timeIntoWindup = this.duelEnemyWindupDuration - this.duelEnemyTimer;
        if (timeIntoWindup >= this.duelEnemyWindupDuration - DUEL_PARRY_WINDOW) {
          this.duelEnemyState = 'stumbling';
          this.duelEnemyTimer = DUEL_STUMBLE_TICKS;
          this.ctx.audio.sfx('clash');
          this.duelClashFlash = 8;
          this.duelPlayerRecovery = DUEL_PARRY_RECOVERY;
        }
      }
    }
  }

  private updateDuel(): void {
    this.duelTimeUsed += 1;
    if (this.duelPlayerRecovery > 0) this.duelPlayerRecovery -= 1;
    if (this.duelClashFlash > 0) this.duelClashFlash -= 1;

    this.duelEnemyTimer -= 1;
    if (this.duelEnemyState === 'idle' && this.duelEnemyTimer <= 0) {
      // Start winding up.
      const dur =
        DUEL_WINDUP_TICKS_MIN +
        this.ctx.random.int(0, DUEL_WINDUP_TICKS_MAX - DUEL_WINDUP_TICKS_MIN);
      this.duelEnemyState = 'winding-up';
      this.duelEnemyTimer = dur;
      this.duelEnemyWindupDuration = dur;
    } else if (this.duelEnemyState === 'winding-up' && this.duelEnemyTimer <= 0) {
      // Enemy strike lands — player takes damage if they didn't parry.
      this.duelPlayerHP -= 1;
      this.ctx.audio.sfx('strike');
      this.duelClashFlash = 8;
      this.duelEnemyState = 'recovering';
      this.duelEnemyTimer = 30;
      if (this.duelPlayerHP <= 0) {
        this.duelPlayerWon = false;
        this.endDuel();
        return;
      }
    } else if (this.duelEnemyState === 'stumbling' && this.duelEnemyTimer <= 0) {
      // Stumble passed without player striking — back to idle.
      this.duelEnemyState = 'idle';
      this.duelEnemyTimer = 30 + this.ctx.random.int(0, 60);
    } else if (this.duelEnemyState === 'recovering' && this.duelEnemyTimer <= 0) {
      this.duelEnemyState = 'idle';
      this.duelEnemyTimer = 60 + this.ctx.random.int(0, 60);
    }

    if (this.duelTimeUsed >= DUEL_DURATION_MAX) {
      // Safety timeout — whoever has more HP wins, ties go to enemy.
      this.duelPlayerWon = this.duelPlayerHP > this.duelEnemyHP;
      this.endDuel();
    }
  }

  private endDuel(): void {
    if (this.duelPlayerWon && this.boardingEnemy) {
      // Award sinking + bonus.
      this.score += ENEMY_SCORE[this.boardingEnemy.kind] + 300;
      this.ctx.audio.sfx('board-win');
      // Remove the boarded enemy from the world.
      this.enemies = this.enemies.filter((e) => e !== this.boardingEnemy);
    } else {
      // Player loses — take 1 HP, enemy stays.
      this.takePlayerDamage(1);
    }
    if (this.playerHP <= 0) return; // game-over already triggered
    this.phase = 'boarding-transition-out';
    this.transitionTicks = 0;
  }

  private renderDuel(g: CanvasRenderingContext2D): void {
    // Sky gradient.
    g.fillStyle = DUEL_SKY;
    g.fillRect(0, 0, WIDTH, HEIGHT / 2);
    g.fillStyle = DUEL_SKY_DARK;
    g.fillRect(0, HEIGHT / 3, WIDTH, HEIGHT / 6);
    g.fillStyle = DUEL_HORIZON;
    g.fillRect(0, HEIGHT / 2, WIDTH, 4);
    // Deck.
    g.fillStyle = DUEL_DECK;
    g.fillRect(0, HEIGHT / 2 + 4, WIDTH, HEIGHT / 2 - 4);
    g.fillStyle = DUEL_DECK_DARK;
    for (let x = 0; x < WIDTH; x += 24) {
      g.fillRect(x, HEIGHT / 2 + 4, 2, HEIGHT / 2 - 4);
    }

    // Pirates.
    this.drawDuelPirate(g, 140, HEIGHT / 2 - 20, true);
    this.drawDuelPirate(g, WIDTH - 140, HEIGHT / 2 - 20, false);

    // Clash flash overlay.
    if (this.duelClashFlash > 0) {
      g.globalAlpha = (this.duelClashFlash / 8) * 0.5;
      g.fillStyle = DUEL_CLASH_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    // HUD: HP bars on each side + control hint.
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';
    g.fillStyle = HUD_TEXT;
    g.textAlign = 'left';
    g.fillText('YOU', 8, 8);
    g.textAlign = 'right';
    g.fillText('FOE', WIDTH - 8, 8);
    for (let i = 0; i < DUEL_HP_MAX; i++) {
      g.fillStyle = i < this.duelPlayerHP ? HUD_HP_FULL : 'rgba(255,255,255,0.2)';
      g.fillRect(8 + i * 10, 22, 6, 6);
      g.fillStyle = i < this.duelEnemyHP ? HUD_HP_LOW : 'rgba(255,255,255,0.2)';
      g.fillRect(WIDTH - 14 - i * 10, 22, 6, 6);
    }
    g.textAlign = 'center';
    g.fillStyle = HUD_TEXT;
    g.fillText('Z STRIKE   X PARRY', WIDTH / 2, 8);

    // State indicator over the enemy.
    if (this.duelEnemyState === 'winding-up') {
      g.fillStyle = HUD_HP_LOW;
      g.fillText('!', WIDTH - 140, HEIGHT / 2 - 60);
    } else if (this.duelEnemyState === 'stumbling') {
      g.fillStyle = HUD_SCORE;
      g.fillText('OPEN', WIDTH - 140, HEIGHT / 2 - 60);
    }
    g.textAlign = 'left';
  }

  private drawDuelPirate(
    g: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    isPlayer: boolean
  ): void {
    const bodyColor = isPlayer ? DUEL_PIRATE_PLAYER : DUEL_PIRATE_ENEMY;
    // Body.
    g.fillStyle = '#000';
    g.fillRect(cx - 7, cy + 1, 16, 32);
    g.fillStyle = bodyColor;
    g.fillRect(cx - 8, cy, 16, 32);
    // Head.
    g.fillStyle = '#e0c0a0';
    g.fillRect(cx - 5, cy - 10, 10, 10);
    // Hat.
    g.fillStyle = '#1a1a24';
    g.fillRect(cx - 7, cy - 12, 14, 4);
    // Sword.
    g.fillStyle = DUEL_SWORD;
    if (isPlayer) {
      g.fillRect(cx + 8, cy + 4, 22, 3);
      g.fillStyle = DUEL_SWORD_GLINT;
      g.fillRect(cx + 26, cy + 4, 4, 1);
    } else {
      g.fillRect(cx - 30, cy + 4, 22, 3);
      g.fillStyle = DUEL_SWORD_GLINT;
      g.fillRect(cx - 30, cy + 4, 4, 1);
    }
    // Legs.
    g.fillStyle = bodyColor;
    g.fillRect(cx - 6, cy + 32, 5, 8);
    g.fillRect(cx + 1, cy + 32, 5, 8);
  }

  // ==========================================================================
  // ARTILLERY PHASE
  // ==========================================================================

  private startArtillery(): void {
    this.phase = 'artillery';
    this.artPlayerHP = ART_HP_MAX;
    this.artEnemyHP = ART_HP_MAX;
    this.artTurn = 'player';
    this.artAngle = 45;
    this.artPower = 70;
    this.artProjectile = null;
    this.artHitFlash = 0;
    this.artResolveTimer = 0;
    this.artPlayerWon = false;
    // PRNG-placed barrels in the middle of the channel.
    this.artBarrels = [];
    for (let i = 0; i < ART_BARRELS; i++) {
      const x = WIDTH / 2 - 80 + this.ctx.random.int(0, 160);
      this.artBarrels.push({ x, alive: true });
    }
  }

  private artilleryInput(state: InputState): void {
    if (this.artTurn !== 'player') return;
    if (state.left) this.artAngle = Math.max(ART_MIN_ANGLE, this.artAngle - 0.8);
    if (state.right) this.artAngle = Math.min(ART_MAX_ANGLE, this.artAngle + 0.8);
    if (state.up) this.artPower = Math.min(ART_MAX_POWER, this.artPower + 0.8);
    if (state.down) this.artPower = Math.max(ART_MIN_POWER, this.artPower - 0.8);
    if (state.justPressed.has('fireA')) {
      this.fireArtProjectile(true);
    }
  }

  private fireArtProjectile(fromPlayer: boolean): void {
    const angleRad = (this.artAngle * Math.PI) / 180;
    const speed = this.artPower / 8;
    const dir = fromPlayer ? 1 : -1;
    const startX = fromPlayer ? ART_PLAYER_X + 18 : ART_ENEMY_X - 18;
    const startY = ART_GROUND_Y - ART_SHIP_H / 2;
    this.artProjectile = {
      x: startX,
      y: startY,
      vx: Math.cos(angleRad) * speed * dir,
      vy: -Math.sin(angleRad) * speed,
    };
    this.artTurn = 'projectile';
    this.ctx.audio.sfx('cannon');
  }

  private updateArtillery(): void {
    if (this.artHitFlash > 0) this.artHitFlash -= 1;

    if (this.artTurn === 'enemy') {
      // Enemy picks angle + power. Accuracy improves as player HP drops:
      // baseline noisy aim, then tighter when player is weak (rubber-band).
      const aimNoise = 0.18 - 0.05 * (ART_HP_MAX - this.artPlayerHP); // less noise as player low
      // Solve approximately for angle that lands on player (very rough,
      // physics-aware enough to feel intentional). Then add noise.
      const dx = ART_PLAYER_X - ART_ENEMY_X;
      const baseAngle = 45 + (this.ctx.random.next() - 0.5) * 30 * aimNoise;
      this.artAngle = Math.max(ART_MIN_ANGLE, Math.min(ART_MAX_ANGLE, baseAngle));
      // Power scales loosely with distance.
      const basePower = 70 + (this.ctx.random.next() - 0.5) * 30 * aimNoise;
      this.artPower = Math.max(ART_MIN_POWER, Math.min(ART_MAX_POWER, basePower));
      // Briefly pause so the player can see what the enemy chose.
      // Use artResolveTimer as a delay counter.
      this.artResolveTimer += 1;
      if (this.artResolveTimer >= 30) {
        this.artResolveTimer = 0;
        // Mark dx as referenced.
        void dx;
        this.fireArtProjectile(false);
      }
      return;
    }

    if (this.artTurn === 'projectile' && this.artProjectile) {
      const p = this.artProjectile;
      // Wind influence — the open-sea wind vector affects horizontal drift.
      // Treat the wind direction as a 2D vector and apply its x-component.
      const windVecX = Math.cos(this.windDirection) * 0.04;
      p.vx += windVecX;
      p.vy += ART_GRAVITY;
      p.x += p.vx;
      p.y += p.vy;

      // Off-screen left/right or hit ground.
      if (p.y >= ART_GROUND_Y || p.x < 0 || p.x > WIDTH) {
        // Splash audio if landed in water (off-ship).
        this.ctx.audio.sfx('art-miss');
        this.artProjectile = null;
        this.advanceArtTurn();
        return;
      }

      // Hit-test barrels.
      for (const b of this.artBarrels) {
        if (!b.alive) continue;
        if (
          p.x >= b.x - ART_BARREL_W / 2 &&
          p.x <= b.x + ART_BARREL_W / 2 &&
          p.y >= ART_GROUND_Y - ART_BARREL_H
        ) {
          b.alive = false;
          this.artProjectile = null;
          this.ctx.audio.sfx('art-miss');
          this.advanceArtTurn();
          return;
        }
      }
      // Hit-test ships.
      // Player ship.
      if (
        p.x >= ART_PLAYER_X - ART_SHIP_W / 2 &&
        p.x <= ART_PLAYER_X + ART_SHIP_W / 2 &&
        p.y >= ART_GROUND_Y - ART_SHIP_H
      ) {
        this.artPlayerHP -= 1;
        this.artHitFlash = 12;
        this.ctx.audio.sfx('art-hit');
        this.artProjectile = null;
        if (this.artPlayerHP <= 0) {
          this.artPlayerWon = false;
          this.endArtillery();
          return;
        }
        this.advanceArtTurn();
        return;
      }
      // Enemy ship.
      if (
        p.x >= ART_ENEMY_X - ART_SHIP_W / 2 &&
        p.x <= ART_ENEMY_X + ART_SHIP_W / 2 &&
        p.y >= ART_GROUND_Y - ART_SHIP_H
      ) {
        this.artEnemyHP -= 1;
        this.artHitFlash = 12;
        this.ctx.audio.sfx('art-hit');
        this.artProjectile = null;
        if (this.artEnemyHP <= 0) {
          this.artPlayerWon = true;
          this.endArtillery();
          return;
        }
        this.advanceArtTurn();
        return;
      }
    }
  }

  private advanceArtTurn(): void {
    // After projectile resolves, the OTHER side takes its turn.
    if (this.artTurn === 'projectile') {
      // We just resolved a projectile — alternate based on who fired.
      // Simpler: track last shooter. We stored it implicitly by looking
      // at projectile.vx sign at fire-time, but we cleared the projectile.
      // Track with a counter instead — use artResolveTimer parity.
      this.artResolveTimer = 0;
      // Toggle: if last was player turn (which became 'projectile' after
      // they fired), now it's the enemy. We need a way to know — derive
      // from the projectile direction at fire-time. Since we already
      // cleared the projectile, store who-fired-last in another field.
      // Easier: alternate via an internal flag using parity of a counter.
      this.artLastShooter = this.artLastShooter === 'player' ? 'enemy' : 'player';
      this.artTurn = this.artLastShooter === 'player' ? 'enemy' : 'player';
    }
  }
  private artLastShooter: 'player' | 'enemy' = 'player';

  private endArtillery(): void {
    if (this.artPlayerWon && this.boardingEnemy) {
      this.score += ENEMY_SCORE[this.boardingEnemy.kind] + 300;
      this.ctx.audio.sfx('board-win');
      this.enemies = this.enemies.filter((e) => e !== this.boardingEnemy);
    } else {
      this.takePlayerDamage(1);
    }
    if (this.playerHP <= 0) return;
    this.phase = 'boarding-transition-out';
    this.transitionTicks = 0;
  }

  private renderArtillery(g: CanvasRenderingContext2D): void {
    // Sky.
    g.fillStyle = ART_SKY;
    g.fillRect(0, 0, WIDTH, ART_GROUND_Y);
    g.fillStyle = ART_SKY_LIGHT;
    for (let y = 0; y < ART_GROUND_Y; y += 30) g.fillRect(0, y, WIDTH, 1);
    // Water.
    g.fillStyle = ART_WATER;
    g.fillRect(0, ART_GROUND_Y, WIDTH, HEIGHT - ART_GROUND_Y);
    g.fillStyle = SEA_LIGHT;
    for (let x = 0; x < WIDTH; x += 16) {
      g.fillRect(x, ART_GROUND_Y + 4, 8, 1);
    }

    // Barrels.
    for (const b of this.artBarrels) {
      if (!b.alive) continue;
      g.fillStyle = ART_BARREL;
      g.fillRect(b.x - ART_BARREL_W / 2, ART_GROUND_Y - ART_BARREL_H, ART_BARREL_W, ART_BARREL_H);
      g.fillStyle = ART_BARREL_BAND;
      g.fillRect(b.x - ART_BARREL_W / 2, ART_GROUND_Y - ART_BARREL_H + 4, ART_BARREL_W, 2);
      g.fillRect(b.x - ART_BARREL_W / 2, ART_GROUND_Y - 6, ART_BARREL_W, 2);
    }

    // Ships.
    this.drawArtilleryShip(g, ART_PLAYER_X, true);
    this.drawArtilleryShip(g, ART_ENEMY_X, false);

    // Projectile + trajectory dots.
    if (this.artProjectile) {
      g.fillStyle = ART_TRAJECTORY_DOT;
      g.fillRect(this.artProjectile.x - 2, this.artProjectile.y - 2, 4, 4);
    }
    // Trajectory preview during player turn.
    if (this.artTurn === 'player') {
      this.drawTrajectoryPreview(g);
    }

    if (this.artHitFlash > 0) {
      g.globalAlpha = (this.artHitFlash / 12) * 0.4;
      g.fillStyle = ART_HIT_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    // HUD.
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';
    g.textAlign = 'left';
    g.fillStyle = HUD_TEXT;
    g.fillText('YOU', 8, 8);
    g.textAlign = 'right';
    g.fillText('FOE', WIDTH - 8, 8);
    for (let i = 0; i < ART_HP_MAX; i++) {
      g.fillStyle = i < this.artPlayerHP ? HUD_HP_FULL : 'rgba(255,255,255,0.2)';
      g.fillRect(8 + i * 10, 22, 6, 6);
      g.fillStyle = i < this.artEnemyHP ? HUD_HP_LOW : 'rgba(255,255,255,0.2)';
      g.fillRect(WIDTH - 14 - i * 10, 22, 6, 6);
    }
    // Angle + power readout.
    g.textAlign = 'center';
    g.fillStyle = HUD_SCORE;
    g.fillText(`ANG ${Math.floor(this.artAngle)}°   PWR ${Math.floor(this.artPower)}`, WIDTH / 2, 8);
    g.fillStyle = HUD_TEXT;
    g.fillText(this.artTurn === 'player' ? 'YOUR TURN — Z FIRE' : 'INCOMING…', WIDTH / 2, 20);
    g.textAlign = 'left';
  }

  private drawArtilleryShip(g: CanvasRenderingContext2D, cx: number, isPlayer: boolean): void {
    const y = ART_GROUND_Y - ART_SHIP_H;
    g.fillStyle = '#000';
    g.fillRect(cx - ART_SHIP_W / 2 + 1, y + 1, ART_SHIP_W, ART_SHIP_H);
    g.fillStyle = isPlayer ? HULL_BODY : ENEMY_HULL;
    g.fillRect(cx - ART_SHIP_W / 2, y, ART_SHIP_W, ART_SHIP_H);
    g.fillStyle = isPlayer ? HULL_DARK : ENEMY_HULL_DARK;
    g.fillRect(cx - ART_SHIP_W / 2, y + ART_SHIP_H - 4, ART_SHIP_W, 4);
    // Mast.
    g.fillStyle = isPlayer ? HULL_DARK : ENEMY_HULL_DARK;
    g.fillRect(cx - 1, y - 24, 2, 24);
    // Sail.
    g.fillStyle = isPlayer ? SAIL_WHITE : ENEMY_SAIL;
    g.fillRect(cx - 8, y - 22, 16, 14);
    // Cannon barrel pointing in fire direction.
    g.fillStyle = CANNON_BARREL;
    const barX = isPlayer ? cx + ART_SHIP_W / 2 : cx - ART_SHIP_W / 2 - 6;
    g.fillRect(barX, y + 4, 6, 4);
  }

  private drawTrajectoryPreview(g: CanvasRenderingContext2D): void {
    // 12 dots along the predicted parabola — gives the player a guide.
    const angleRad = (this.artAngle * Math.PI) / 180;
    const speed = this.artPower / 8;
    const startX = ART_PLAYER_X + 18;
    const startY = ART_GROUND_Y - ART_SHIP_H / 2;
    let vx = Math.cos(angleRad) * speed;
    let vy = -Math.sin(angleRad) * speed;
    const windVecX = Math.cos(this.windDirection) * 0.04;
    let x = startX;
    let y = startY;
    g.fillStyle = ART_TRAJECTORY_DOT;
    for (let i = 0; i < 36; i++) {
      vx += windVecX;
      vy += ART_GRAVITY;
      x += vx;
      y += vy;
      if (y >= ART_GROUND_Y || x < 0 || x > WIDTH) break;
      if (i % 3 === 0) g.fillRect(x - 1, y - 1, 2, 2);
    }
  }

  // ==========================================================================
  // GAME-OVER
  // ==========================================================================

  private renderGameOver(g: CanvasRenderingContext2D): void {
    g.fillStyle = 'rgba(0,0,0,0.78)';
    g.fillRect(0, 0, WIDTH, HEIGHT);
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = '24px ui-monospace, monospace';
    g.fillStyle = HUD_HP_LOW;
    g.fillText('SHIP LOST', WIDTH / 2, HEIGHT / 2 - 30);
    g.font = '12px ui-monospace, monospace';
    g.fillStyle = HUD_SCORE;
    g.fillText(`SCORE ${String(this.finalScore).padStart(6, '0')}`, WIDTH / 2, HEIGHT / 2);
    g.font = '10px ui-monospace, monospace';
    g.fillStyle = HUD_TEXT;
    g.fillText('PRESS Z TO RETRY', WIDTH / 2, HEIGHT / 2 + 30);
  }
}
